import { SpotlightItem, SpotlightItemType } from '@thoughtspot/global-nav';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { useCallback, useMemo } from 'react';
import {
    SearchCollectionsQuery,
    useSearchCollectionsLazyQuery as _useSearchCollectionsLazyQuery,
} from '../../services/generated/graphql-types';

type CollectionFolder = NonNullable<
    NonNullable<
        NonNullable<SearchCollectionsQuery['Collection_Search']>['Data']
    >['folders']
>[number];

/**
 * Lazily fetches collections visible to the user and converts them to
 * SpotlightItem[]. The fetch is triggered by calling activate().
 *
 * Used when the user selects the "Collections" filter in the command palette.
 */
export function useSpotlightCollectionItems() {
    const [
        fetchCollections,
        { data, loading },
    ] = _useSearchCollectionsLazyQuery();

    const activate = useCallback(() => {
        fetchCollections({
            fetchPolicy: 'network-only',
            variables: {
                data: {
                    offset: 0,
                    batchsize: 100,
                    name_pattern: '%',
                    include_metadata: false,
                    sort_options: {
                        field_name: 'MODIFIED',
                        order: 'DESC',
                    },
                },
            },
        });
    }, [fetchCollections]);

    const collectionItems = useMemo<SpotlightItem[]>(() => {
        const folders = data?.Collection_Search?.Data?.folders;
        if (!folders) return [];
        return folders
            .filter(
                (f: CollectionFolder): f is NonNullable<CollectionFolder> =>
                    !!f?.id && !!f?.name,
            )
            .map((folder: NonNullable<CollectionFolder>) => ({
                id: `collection-spotlight-${folder.id}`,
                label: folder.name!,
                description: folder.authorName
                    ? `by ${folder.authorName}`
                    : undefined,
                icon: IconID.COLLECTION,
                type: SpotlightItemType.NAVIGATION,
                path: `/insights/collections/${folder.id}`,
                keywords: ['collection', 'folder'],
            }));
    }, [data]);

    return { collectionItems, collectionItemsLoading: loading, activate };
}
